import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  LinearProgress,
  Chip,
  Grid,
  Stack,
  Paper,
  Divider,
  Theme,
  useTheme,
} from '@mui/material';
import { UploadStage, UploadFileStatus } from './hooks/useMediaUpload';
import { useFeatureFlags } from '../../../contexts/FeatureFlagsContext';
import { FeatureFlags } from '../../../flags';

interface UploadDialogProps {
  open: boolean;
  hidePreview?: boolean;
  isBatchPolling?: boolean; // NEW: Add flag for batch polling mode
  uploadProgress: {
    totalFiles: number;
    completedFiles: number;
    currentFile: UploadFileStatus;
    remainingFiles: string[];
    failedFiles: { name: string; error: string }[];
  };
}

// Map of stages to friendly display names
const stageLabels: Record<UploadStage, string> = {
  preparing: 'Preparing',
  reading: 'Reading',
  uploading: 'Creating',
  sending: 'Uploading',
  processing: 'Processing',
  complete: 'Complete',
  error: 'Error',
};

// Map of stages to colors
function getStageColor({
  stage,
  theme,
}: {
  stage: UploadStage;
  theme: Theme;
}): string {
  const stageColors: Record<UploadStage, string> = {
    preparing: theme.palette.grey[400],
    reading: theme.palette.grey[400],
    uploading: theme.palette.secondary.medium!, // since not all palettes currently implement a mid value, we need to assert that secondary has one defined here
    sending: theme.palette.secondary.medium!,
    processing: theme.palette.secondary.medium!,
    complete: theme.palette.secondary.medium!,
    error: theme.palette.error.medium!,
  };
  return stageColors[stage];
}

// Define the stages in order
const stages: UploadStage[] = [
  'preparing',
  'reading',
  'uploading',
  'sending',
  'processing',
  'complete',
];

const StageChip = ({
  stage,
  currentStage,
  completed,
}: {
  stage: UploadStage;
  currentStage: UploadStage;
  completed: boolean;
}) => {
  // Determine the status based on the current stage
  const isActive = stage === currentStage;
  const isPast = stages.indexOf(stage) < stages.indexOf(currentStage);

  let color: 'default' | 'primary' | 'success' | 'error' = 'default';
  let variant: 'filled' | 'outlined' = 'outlined';

  if (completed && stage === 'complete') {
    color = 'success';
    variant = 'filled';
  } else if (isActive) {
    color = 'primary';
    variant = 'filled';
  } else if (isPast) {
    color = 'success';
    variant = 'outlined';
  } else if (currentStage === 'error' && stage === 'error') {
    color = 'error';
    variant = 'filled';
  }

  return (
    <Chip
      label={stageLabels[stage]}
      color={color}
      variant={variant}
      size="small"
      sx={{
        fontWeight: isActive ? 'bold' : 'normal',
        minWidth: '85px',
      }}
    />
  );
};

const UploadDialog: React.FC<UploadDialogProps> = ({
  open,
  hidePreview,
  isBatchPolling = false,
  uploadProgress,
}) => {
  const theme = useTheme();
  const { hasFeature } = useFeatureFlags();
  const isDebugMode = hasFeature(FeatureFlags.NGW_MEDIA_DEBUG);

  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [smoothProgress, setSmoothProgress] = useState(0);

  const {
    totalFiles,
    completedFiles,
    currentFile,
    remainingFiles,
    failedFiles,
  } = uploadProgress;

  // Calculate overall progress
  const filesProgress = (completedFiles / totalFiles) * 100;

  // In simple mode, we want to ensure the progress bar moves smoothly
  // even if the actual progress isn't changing much
  const getTargetProgress = useCallback(() => {
    if (isDebugMode) {
      // In debug mode, use the detailed progress calculation
      const currentFileProgress = currentFile.progress / 100;
      return (filesProgress + currentFileProgress * (100 / totalFiles)) / 1.05;
    } else {
      // In simple mode, ensure progress is always moving forward
      // Map the various stages to a smoother progress curve
      let baseProgress = 0;

      // Special handling for batch polling mode
      if (isBatchPolling && currentFile.stage === 'processing') {
        // In batch polling mode during processing, progress should reflect
        // the overall completion percentage more directly
        baseProgress =
          filesProgress + ((completedFiles + 0.5) / totalFiles) * 100 * 0.9; // 90% of expected final value
      } else {
        // Standard progress calculation
        switch (currentFile.stage) {
          case 'preparing':
          case 'waiting':
          case 'reading':
            // Early stages - map to 0-25%
            baseProgress = filesProgress + currentFile.progress / 4;
            break;
          case 'uploading':
          case 'sending':
            // Middle stages - map to 25-75%
            baseProgress = filesProgress + 25 + currentFile.progress / 2;
            break;
          case 'processing':
            // Processing stage - map to 75-95%
            baseProgress = filesProgress + 75 + currentFile.progress / 5;
            break;
          case 'complete':
            // Complete - full 100%
            baseProgress = 100;
            break;
          case 'error':
            // Error - stop at current progress
            baseProgress = displayedProgress;
            break;
        }
      }

      // Ensure progress never goes backward and always keeps moving
      return Math.max(displayedProgress, Math.min(baseProgress, 99.5));
    }
  }, [
    isDebugMode,
    filesProgress,
    currentFile.stage,
    currentFile.progress,
    totalFiles,
    completedFiles,
    displayedProgress,
    isBatchPolling,
  ]);

  // Smoothly animate progress for both current file and overall progress
  useEffect(() => {
    if (!open) {
      setDisplayedProgress(0);
      setSmoothProgress(0);
      return;
    }

    const targetProgress = getTargetProgress();

    // Smooth animation for overall progress
    if (displayedProgress < targetProgress) {
      const increment = Math.max(
        0.2,
        (targetProgress - displayedProgress) / 20,
      );
      const delay = 25;

      const timer = setTimeout(() => {
        setDisplayedProgress((current) => {
          return Math.min(current + increment, targetProgress);
        });
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [open, getTargetProgress, displayedProgress]);

  // Smooth animation for current file progress
  useEffect(() => {
    if (!open) {
      setSmoothProgress(0);
      return;
    }

    // Smooth animation for current file progress
    if (smoothProgress < currentFile.progress) {
      const increment = Math.max(
        0.5,
        (currentFile.progress - smoothProgress) / 10,
      );
      const delay = 30;

      const timer = setTimeout(() => {
        setSmoothProgress((current) => {
          return Math.min(current + increment, currentFile.progress);
        });
      }, delay);

      return () => clearTimeout(timer);
    }

    // Reset smoothProgress when switching to new file
    if (smoothProgress > currentFile.progress + 5) {
      setSmoothProgress(currentFile.progress);
    }
  }, [open, currentFile.progress, smoothProgress]);

  // Render the debug view with detailed information
  const renderDebugView = () => {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          {!hidePreview && currentFile.previewUrl && (
            <Paper
              elevation={2}
              sx={{
                width: 120,
                height: 120,
                borderRadius: 2,
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              <img
                src={currentFile.previewUrl}
                alt="Upload preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Paper>
          )}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 1,
              }}
            >
              <Typography variant="h6" gutterBottom fontWeight="medium">
                {`Uploading ${totalFiles} ${totalFiles === 1 ? 'file' : 'files'}`}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight="medium"
              >
                {completedFiles}/{totalFiles} complete
              </Typography>
            </Box>

            {/* File name and status */}
            <Typography variant="body1" gutterBottom fontWeight="medium">
              {currentFile.name || 'Preparing...'}
            </Typography>

            {/* Current status text */}
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {isBatchPolling && currentFile.stage === 'processing'
                ? `Batch processing: ${completedFiles} of ${totalFiles} files complete`
                : currentFile.statusText || stageLabels[currentFile.stage]}
              {currentFile.stage === 'processing' &&
                ' (this may take a moment)'}
            </Typography>

            {/* Status indicators */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                mb: 1,
                flexWrap: 'wrap',
                '& .MuiChip-root': {
                  my: 0.5,
                },
              }}
            >
              {stages.map((stage) => (
                <StageChip
                  key={stage}
                  stage={stage}
                  currentStage={currentFile.stage}
                  completed={currentFile.stage === 'complete'}
                />
              ))}
              {currentFile.stage === 'error' && (
                <StageChip
                  stage="error"
                  currentStage={currentFile.stage}
                  completed={false}
                />
              )}
            </Stack>

            {/* Progress bars */}
            <Box sx={{ width: '100%', position: 'relative' }}>
              {/* Progress track with animated pulse effect during processing */}
              <Box
                sx={{
                  height: 10,
                  borderRadius: 5,
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  ...(currentFile.stage === 'processing' && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    },
                    '@keyframes pulse': {
                      '0%': { transform: 'translateX(-100%)' },
                      '100%': { transform: 'translateX(100%)' },
                    },
                  }),
                }}
              >
                {/* Current file progress */}
                <Box
                  sx={{
                    height: '100%',
                    borderRadius: 5,
                    width: `${smoothProgress}%`,
                    backgroundColor: getStageColor({
                      stage: currentFile.stage,
                      theme,
                    }),
                    transition: 'width 0.2s ease-out',
                  }}
                />
              </Box>

              {currentFile.stage === 'waiting' ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Waiting for autosave to complete...
                </Typography>
              ) : (
                // Overall progress (shown as a thin line below)
                <LinearProgress
                  variant="determinate"
                  value={displayedProgress}
                  sx={{
                    height: 3,
                    borderRadius: 5,
                    mt: 0.5,
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    '& .MuiLinearProgress-bar': {
                      transition: 'none',
                    },
                  }}
                />
              )}
            </Box>

            {/* Error message */}
            {currentFile.stage === 'error' && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                Error: {currentFile.error || 'Upload failed'}
              </Typography>
            )}

            {/* Batch polling mode indicator */}
            {isBatchPolling && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 1, fontStyle: 'italic' }}
              >
                Batch processing mode: Optimizing API calls
              </Typography>
            )}
          </Box>
        </Box>
        {/* Queue information */}
        {(remainingFiles.length > 0 || failedFiles.length > 0) && (
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ my: 1 }} />
            <Grid container spacing={2}>
              {remainingFiles.length > 0 && (
                <Grid size={6}>
                  <Typography variant="body2" fontWeight="medium">
                    Queued: {remainingFiles.length} files
                  </Typography>
                  <Box sx={{ mt: 0.5, maxHeight: '60px', overflow: 'auto' }}>
                    {remainingFiles.slice(0, 3).map((file, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {file}
                      </Typography>
                    ))}
                    {remainingFiles.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        +{remainingFiles.length - 3} more...
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}

              {failedFiles.length > 0 && (
                <Grid size={6}>
                  <Typography variant="body2" fontWeight="medium" color="error">
                    Failed: {failedFiles.length} files
                  </Typography>
                  <Box sx={{ mt: 0.5, maxHeight: '60px', overflow: 'auto' }}>
                    {failedFiles.slice(0, 3).map((file, index) => (
                      <Typography
                        key={index}
                        variant="caption"
                        display="block"
                        color="error"
                      >
                        {file.name}
                      </Typography>
                    ))}
                    {failedFiles.length > 3 && (
                      <Typography variant="caption" color="error">
                        +{failedFiles.length - 3} more...
                      </Typography>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  // Render the simplified view for customers
  const renderSimpleView = () => {
    // Special handling for batch processing phase
    const isBatchProcessing =
      isBatchPolling && currentFile.stage === 'processing';

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!hidePreview && currentFile.previewUrl && (
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                flexShrink: 0,
                border: '1px solid rgba(0, 0, 0, 0.12)',
              }}
            >
              <img
                src={currentFile.previewUrl}
                alt="Upload preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              {currentFile.stage === 'error'
                ? 'Upload Error'
                : `Uploading ${totalFiles} ${totalFiles === 1 ? 'file' : 'files'}`}
            </Typography>

            {currentFile.stage === 'waiting' ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Waiting for autosave to complete...
              </Typography>
            ) : (
              <LinearProgress
                variant="determinate"
                value={displayedProgress}
                sx={{
                  mb: 1,
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.08)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor:
                      currentFile.stage === 'error'
                        ? 'error.medium'
                        : undefined,
                    transition: 'width 0.2s ease-out',
                  },
                  ...(isBatchProcessing && {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background:
                        'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                      animation: 'pulse 1.5s ease-in-out infinite',
                    },
                    '@keyframes pulse': {
                      '0%': { transform: 'translateX(-100%)' },
                      '100%': { transform: 'translateX(100%)' },
                    },
                  }),
                }}
              />
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                {currentFile.stage === 'error'
                  ? currentFile.error || 'Upload failed'
                  : `${completedFiles} of ${totalFiles} complete`}
              </Typography>

              {currentFile.stage !== 'error' && (
                <Typography variant="body2" color="text.secondary">
                  {Math.round(displayedProgress)}%
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {failedFiles.length > 0 && (
          <Typography variant="body2" color="error">
            {failedFiles.length} {failedFiles.length === 1 ? 'file' : 'files'}{' '}
            failed to upload
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      maxWidth={isDebugMode ? 'md' : 'sm'}
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogContent sx={{ p: isDebugMode ? 3 : 2 }}>
        {isDebugMode ? renderDebugView() : renderSimpleView()}
      </DialogContent>
    </Dialog>
  );
};

export default UploadDialog;
